import { useEffect, useState } from "react";

/**
 * WorldCupCountdownPulse
 * Lightweight pulse derived from a phase (pre/anthem/late/penalty).
 * No realtime — phase comes from the host route, intensity is local.
 */
export type CountdownPhase = "idle" | "pre_match" | "anthem" | "late_game" | "penalties";

export type Pulse = {
  phase: CountdownPhase;
  /** 0..1 — UI uses it to scale glow / heartbeat */
  intensity: number;
  beatMs: number; // animation cycle in ms
  label: string;
};

const PRESETS: Record<CountdownPhase, Omit<Pulse, "phase" | "label">> = {
  idle:       { intensity: 0,    beatMs: 1800 },
  pre_match:  { intensity: 0.45, beatMs: 1500 },
  anthem:     { intensity: 0.7,  beatMs: 1100 },
  late_game:  { intensity: 0.9,  beatMs: 800  },
  penalties:  { intensity: 1,    beatMs: 600  },
};

const LABELS: Record<CountdownPhase, string> = {
  idle: "",
  pre_match: "wc_phase_pre",
  anthem: "wc_phase_anthem",
  late_game: "wc_phase_late",
  penalties: "wc_phase_pen",
};

export function pulseFor(phase: CountdownPhase): Pulse {
  const p = PRESETS[phase];
  return { phase, label: LABELS[phase], ...p };
}

/**
 * Demo cycler — rotates phases every N seconds. Used on the showcase
 * route to demonstrate the energy ramp.
 */
export function useDemoCountdownPhase(intervalMs = 12_000): CountdownPhase {
  const order: CountdownPhase[] = ["pre_match", "anthem", "late_game", "penalties"];
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setI((x) => (x + 1) % order.length), intervalMs);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs]);
  return order[i];
}
